import { useState, useEffect, useCallback, useMemo } from "react";
import { getBaskets, getClientsWithMargin } from "../../../api/orderInitiation";
import { formatAvailableMargin } from "../utils";

/** Basket list + selection + filter clients from selected baskets. */
export function useBasketsAndClients() {
  const [selectedBasketIndices, setSelectedBasketIndices] = useState(() => new Set());
  const [baskets, setBaskets] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [basketsLoading, setBasketsLoading] = useState(true);
  const [clientsLoading, setClientsLoading] = useState(true);
  const [marginToDeployByClientId, setMarginToDeployByClientId] = useState({});
  const [multiplierByClientId, setMultiplierByClientId] = useState({});
  const [checkedByClientId, setCheckedByClientId] = useState({});

  // Fetch all baskets once on mount
  useEffect(() => {
    setBasketsLoading(true);
    getBaskets()
      .then((data) => {
        const list = data?.response ?? data ?? [];
        setBaskets(Array.isArray(list) ? list : []);
      })
      .catch((err) => console.error("Failed to fetch baskets", err))
      .finally(() => setBasketsLoading(false));
  }, []);

  // Fetch all clients once on mount
  useEffect(() => {
    setClientsLoading(true);
    getClientsWithMargin()
      .then((data) => {
        const list = Array.isArray(data) ? data : data?.response ?? [];
        setAllClients(list);
      })
      .catch((err) => console.error("Failed to fetch clients", err))
      .finally(() => setClientsLoading(false));
  }, []);

  const hasBasketSelected = selectedBasketIndices.size > 0;

  // When no basket selected: show all clients. When basket(s) selected: show only clients in those baskets.
  const filteredClients = useMemo(() => {
    if (!hasBasketSelected) return allClients;

    const selectedCodes = new Set();
    baskets.forEach((basket) => {
      if (selectedBasketIndices.has(basket.id)) {
        (basket.broker_client_codes || []).forEach((code) => selectedCodes.add(code));
      }
    });

    return allClients.filter((client) =>
      selectedCodes.has(client.broker_client_code)
    );
  }, [hasBasketSelected, baskets, selectedBasketIndices, allClients]);

  const toggleBasket = useCallback((id) => {
    setSelectedBasketIndices((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const updateClientRow = useCallback((rowId, field, value) => {
    if (field === "marginToDeploy") {
      let clamped = value;
      const num = parseFloat(value);
      if (value !== "" && !Number.isNaN(num)) {
        if (num > 100) clamped = "100";
        else if (num < 0) clamped = "0";
      }
      setMarginToDeployByClientId((prev) => ({ ...prev, [rowId]: clamped }));
      // Dropping stored multiplier so lots track the new % (blur/tab used to persist
      // the old suggested value and lock the field as "manual").
      setMultiplierByClientId((prev) => {
        if (!(rowId in prev)) return prev;
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
    } else if (field === "multiplier") {
      setMultiplierByClientId((prev) => ({ ...prev, [rowId]: value }));
      // Manual multiplier is independent of %-to-deploy; clear % so required margin
      // is validated against full available margin (see useClientRows marginStatus).
      setMarginToDeployByClientId((prev) => ({ ...prev, [rowId]: "" }));
    } else if (field === "checked") {
      setCheckedByClientId((prev) => ({ ...prev, [rowId]: value }));
    }
  }, []);

  const handleClientHeaderCheckboxChange = useCallback((checked) => {
    setCheckedByClientId((prev) => {
      const next = { ...prev };
      filteredClients.forEach((c) => (next[c.id] = checked));
      return next;
    });
  }, [filteredClients]);

  /** Set margin-to-deploy % for many clients at once (e.g. common “apply to all” field). */
  const applyMarginToDeployToAllClients = useCallback((value, clientIds) => {
    if (!Array.isArray(clientIds) || clientIds.length === 0) return;
    let clamped = value;
    const num = parseFloat(value);
    if (value !== "" && !Number.isNaN(num)) {
      if (num > 100) clamped = "100";
      else if (num < 0) clamped = "0";
    }
    setMarginToDeployByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = clamped;
      });
      return next;
    });
    setMultiplierByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        if (id in next) delete next[id];
      });
      return next;
    });
  }, []);

  /** Set manual multiplier for many clients at once; clears margin % per row (same as editing one multiplier). */
  const applyMultiplierToAllClients = useCallback((value, clientIds) => {
    if (!Array.isArray(clientIds) || clientIds.length === 0) return;
    setMultiplierByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = value;
      });
      return next;
    });
    setMarginToDeployByClientId((prev) => {
      const next = { ...prev };
      clientIds.forEach((id) => {
        next[id] = "";
      });
      return next;
    });
  }, []);

  return {
    baskets,
    selectedBasketIndices,
    hasBasketSelected,
    toggleBasket,
    filteredClients,
    basketsLoading,
    clientsLoading,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
    updateClientRow,
    handleClientHeaderCheckboxChange,
    applyMarginToDeployToAllClients,
    applyMultiplierToAllClients,
  };
}

/** Client rows derived from filtered clients + margin from scrip info + local inputs. */
export function useClientRows(filteredClients, marginRequiredFromMeta, marginToDeployByClientId, multiplierByClientId, checkedByClientId) {
  return useMemo(() => {
    const list = filteredClients ?? [];
    return list.map((item) => {
      const id = item.id;
      // API may return available_margin as number or as { available_margin: number }
      const rawMargin =
        item.available_margin != null &&
        typeof item.available_margin === "object" &&
        "available_margin" in item.available_margin
          ? item.available_margin.available_margin
          : item.available_margin ?? item.AvailableMargin ?? 0;
      const availableMarginRaw =
        Number(String(rawMargin).replace(/,/g, "").trim()) || 0;
      const availableMargin = formatAvailableMargin(rawMargin);
      const marginToDeploy = marginToDeployByClientId[id] ?? "";
      const rawPct = parseFloat(marginToDeploy);
      const pct = Number.isFinite(rawPct) ? Math.min(100, Math.max(0, rawPct)) : 0;
      const maxDeploy = availableMarginRaw * (pct / 100);
      const multiplierInput = multiplierByClientId[id];
      const hasManualMultiplier =
        multiplierInput !== undefined && String(multiplierInput).trim() !== "";

      /** Max whole multipliers that fit in deployable margin (pct of available). */
      let suggestedFromMargin = 1;
      if (marginRequiredFromMeta > 0 && pct > 0) {
        suggestedFromMargin = Math.max(
          0,
          Math.floor(maxDeploy / marginRequiredFromMeta)
        );
      }

      const multiplierValue = hasManualMultiplier
        ? parseInt(String(multiplierInput).trim(), 10) || 0
        : suggestedFromMargin;

      const requiredMarginVal = marginRequiredFromMeta * Math.max(0, multiplierValue);
      const requiredMargin =
        marginRequiredFromMeta > 0
          ? requiredMarginVal.toLocaleString("en-IN", { maximumFractionDigits: 0 })
          : "0";

      let marginStatus = null;
      if (marginRequiredFromMeta > 0) {
        const fitsAvailableMargin = requiredMarginVal <= availableMarginRaw;
        if (!hasManualMultiplier && pct > 0) {
          // Margin to Deploy % is set: validate against that slice of available margin only.
          if (suggestedFromMargin === 0) {
            marginStatus = "red";
          } else {
            marginStatus = requiredMarginVal <= maxDeploy ? "green" : "red";
          }
        } else {
          // Manual multiplier only, or no % (default multiplier in UI): use total available margin.
          marginStatus = fitsAvailableMargin ? "green" : "red";
        }
      }
      return {
        id,
        checked: checkedByClientId[id] ?? false,
        clientCode: item.broker_client_code ?? "—",
        availableMargin,
        marginToDeploy,
        multiplier:
          multiplierInput !== undefined && multiplierInput !== "" ? String(multiplierInput) : "",
        suggestedMultiplier: hasManualMultiplier
          ? "1"
          : pct > 0
            ? String(suggestedFromMargin)
            : "1",
        requiredMargin,
        marginStatus,
      };
    });
  }, [
    filteredClients,
    marginRequiredFromMeta,
    marginToDeployByClientId,
    multiplierByClientId,
    checkedByClientId,
  ]);
}
